import * as Sentry from '@sentry/react';

// this bug happens when we just made release
// it looks like "Cannot read properties of undefined (reading 'length')" in file node_modules/@aws-amplify/auth/lib-esm/Auth.js:1512
export function isAmplifyUserAttrsLengthBug(event: Sentry.Event): boolean {
  const exception = event.exception?.values?.[0];
  if (
    !(
      exception?.type === 'TypeError' &&
      /Cannot read properties of undefined \(reading 'length'\)/.test(exception?.value || '')
    )
  ) {
    return false;
  }

  const frames = exception?.stacktrace?.frames || [];

  const hasAmplifyFrame = frames.some((f) =>
    /(node_modules|\.vite)\/deps\/aws-amplify\.js/.test(f.filename || ''),
  );

  const hasAmplifyAuthFns = frames.some((f) =>
    /AuthClass2\.<anonymous>|user\.getUserData/.test(f.function || ''),
  );

  const isUnhandledRejection = exception?.mechanism?.handled === false || event?.level === 'error';

  return hasAmplifyFrame && hasAmplifyAuthFns && isUnhandledRejection;
}

// Browser-specific `window.onerror` wording for throwing a bare `undefined`/`null`
// (i.e. a non-Error value). Firefox: "uncaught exception: undefined";
// Chromium: "Uncaught undefined".
const NON_ERROR_THROW_PATTERNS: readonly RegExp[] = [
  /^uncaught exception: (undefined|null)$/i,
  /^uncaught (undefined|null)$/i,
];

// WebKit/Safari (incl. "Chrome Mobile iOS", which is WebKit under Apple's iOS
// browser-engine mandate) reports a bare thrown value's raw string form with no
// "Uncaught" wrapper at all — e.g. "ga" (WA-3752). A single opaque token (no
// spaces/punctuation) carries the same zero diagnosable information as a bare
// undefined/null: it's a variable name or coerced primitive, not an authored
// message. Sentence-like messages (e.g. "Cannot read properties of null") stay
// out of this match and remain actionable even without a resolvable frame.
const BARE_TOKEN_PATTERN = /^[\w$]{1,32}$/;

// The two BROWSER-LEVEL global handlers Sentry hooks. Both deliver values that no
// code of ours chose to report, so the same "opaque value, no frame of ours"
// reasoning applies to each: a rejected promise whose reason is a bare token is no
// more diagnosable than a bare throw (WA-3756/WA-3757, Sentry WAWIASSIST-23D/23E
// — 46 events from one Chrome-iOS session, every frame pointing at the document
// URL). `mechanism.type === 'generic'` is deliberately NOT here: that is an
// exception our own code captured on purpose, which we always want to see.
const GLOBAL_HANDLER_MECHANISMS: readonly string[] = ['onerror', 'onunhandledrejection'];

const isGlobalHandlerMechanism = (mechanismType: string | undefined): boolean =>
  GLOBAL_HANDLER_MECHANISMS.includes(mechanismType ?? '');

/**
 * Does any frame point at REAL JavaScript — our bundle/chunk (`.js`/`.mjs`, with
 * or without a cache-busting `?v=`), a source-mapped `.ts`/`.tsx`, or a Blob-URL
 * worker (Sentry replay's compression worker runs from one)? A frame like
 * `/customer/general:187:41` — the DOCUMENT URL, no file extension — is NOT one:
 * WebKit reports browser-injected scripts against the page URL, and our
 * production index.html is 24 lines with no inline script, so no code of ours can
 * live at that line. Shared by the predicates below: a real frame means real
 * debuggable info, and its absence is what makes an event unactionable.
 */
function hasScriptFrame(frames: Sentry.StackFrame[] = []): boolean {
  return frames.some(
    (f) => /\.(m?jsx?|tsx?)(\?|#|$)/.test(f.filename || '') || /^blob:/.test(f.filename || ''),
  );
}

// A stack overflow says nothing about WHERE it happened — every deep recursion
// produces the same sentence — so the frames are the only evidence, and here they
// are never ours (WA-3758/WA-3759, Sentry WAWIASSIST-23F/23G).
const STACK_OVERFLOW_PATTERN = /Maximum call stack size exceeded/i;

/**
 * A `RangeError: Maximum call stack size exceeded` that reached a browser global
 * handler (`window.onerror` or `unhandledrejection` — the injected script that
 * produces it uses both, see WA-3756) from a script that is NOT ours —
 * WA-3758 / WA-3759 (Sentry WAWIASSIST-23F, WAWIASSIST-23G; same family as
 * WA-3672's WAWIASSIST-22F).
 *
 * Observed shape: every frame's filename is a ROUTE PATH (`/login:224:63`,
 * `/article/general:187:41`) rather than a script URL, and the recursion is a
 * mutual pair of minified functions (`Ek` <-> `Gk`, ~45 alternations — a
 * recursive DOM/form tree walker). Those frames cannot be our code: the
 * production document is 24 lines and loads exactly one module from `/assets/`,
 * and we upload source maps (`sentryVitePlugin`), so real bundle frames resolve
 * to `src/**` with function names instead of staying minified. Every occurrence
 * so far is Chrome Mobile iOS on iPhone — WebKit, whose call-stack limit is far
 * smaller than V8's, and where the browser injects its own minified modules into
 * every page and reports their frames against the document URL. So an injected
 * script recurses over our (deep MUI) DOM and overflows; the app is unaffected
 * (the reporting user kept working across routes for 8 minutes), yet each
 * occurrence auto-files a Jira ticket through the high-priority alert rule.
 *
 * NOT the same thing as WA-3672, which was a real overflow *we* caused: rrweb's
 * recursive DOM serialization for session replay. That one was fixed at the
 * source (replay disabled on iOS, `main.tsx`) and is already in production —
 * these events post-date that release and carry no replay.
 *
 * Deliberately narrow: a genuine overflow inside our own code arrives with an
 * `/assets/*.js` (or source-mapped `src/**.tsx`) frame and is therefore still
 * reported, as is one inside the replay worker (`blob:`). Only the frame-less,
 * foreign-script variety is dropped.
 */
export function isForeignScriptStackOverflow(event: Sentry.Event): boolean {
  const exception = event.exception?.values?.[0];
  if (!isGlobalHandlerMechanism(exception?.mechanism?.type)) {
    return false;
  }

  if (!STACK_OVERFLOW_PATTERN.test(`${exception?.type ?? ''}: ${exception?.value ?? ''}`)) {
    return false;
  }

  return !hasScriptFrame(exception?.stacktrace?.frames);
}

/**
 * A bare non-Error value (`undefined`/`null`, or any other opaque single
 * token — WA-3752) that reached a browser GLOBAL handler — `window.onerror` or
 * `unhandledrejection` — with no script frame in its stack. WA-3718 / Sentry
 * WAWIASSIST-236, extended to rejections by WA-3756 / WA-3757 (Sentry
 * WAWIASSIST-23D / 23E: `Error: Ba`, 46 events in 90s from one Chrome-iOS
 * session, all three frames pointing at the document URL — an injected browser
 * script, the same producer as WA-3758's overflow).
 *
 * Such an event carries only the browser's onerror string, or a rejection reason
 * that is a minified identifier: Sentry's frames point at the document URL (e.g.
 * `/customer/general:1`, `https://wawiassist.hssoft.com/:191:41`), so there is no
 * throw site, no stack to source-map, and no way to tell which code failed. It
 * cannot be actioned, yet it auto-files a Jira ticket through the high-priority
 * alert rule (same noise family as WA-3546 / WA-3701).
 *
 * Deliberately narrow — it suppresses ONLY events that are simultaneously:
 *   - raised by a browser global handler per `GLOBAL_HANDLER_MECHANISMS` (never a
 *     `generic` exception our own code captured on purpose),
 *   - a bare `undefined`/`null` value per the known wordings, or a bare opaque
 *     token (no spaces/punctuation) per `BARE_TOKEN_PATTERN`, and
 *   - free of any script frame (a real frame means real debuggable info).
 * Sentence-like messages (e.g. "Cannot read properties of null") never match
 * the token pattern, so they stay reported even without a resolvable frame.
 * The dominant producer of this shape, RxJS's `reportUnhandledError`, no longer
 * reaches this path at all: `installRxJsUnhandledErrorReporting`
 * (`../rxjsUnhandledError/rxjsUnhandledError.helper.ts`) intercepts it and
 * reports a tagged `RxJsUnhandledStreamError` carrying the thrown value, which
 * has real frames and is therefore never matched here. What remains — e.g.
 * React's `reportError` for a non-Error thrown in render — stays equally
 * unactionable, so dropping it costs no diagnosable signal.
 */
export function isStacklessNonErrorThrow(event: Sentry.Event): boolean {
  const exception = event.exception?.values?.[0];
  if (!isGlobalHandlerMechanism(exception?.mechanism?.type)) {
    return false;
  }

  const value = (exception?.value || '').trim();
  const isKnownBareWording = NON_ERROR_THROW_PATTERNS.some((pattern) => pattern.test(value));
  if (!isKnownBareWording && !BARE_TOKEN_PATTERN.test(value)) {
    return false;
  }

  return !hasScriptFrame(exception?.stacktrace?.frames);
}
